import { AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, NavigationExtras, Params, Router, UrlTree } from '@angular/router';
import { Sidebar } from 'primeng/sidebar';
import { BehaviorSubject, Observable, of, Subject, Subscription, switchMap, take, takeUntil, tap } from 'rxjs';
import { CrimeGunService } from 'src/app/services/crime-gun.service';
import { LookupService } from 'src/app/services/lookup.service';
import { SearchResultType } from 'src/app/shared/app.enums';
import { AppUtils } from 'src/app/shared/app.utils';
import { DateRangeFilterComponent } from 'src/app/shared/components/date-range-filter/date-range-filter.component';
import { GeographicFilterRegionComponent } from 'src/app/shared/components/geographic-filter/geographic-filter-region/geographic-filter-region.component';
import { IncludeCDSFilterComponent } from 'src/app/shared/components/include-cds-agencies/include-cds-agencies-filter.component';
import { AuthorizationModel } from 'src/app/shared/models/authorization-model';
import { GeographicFilterRegionModel } from 'src/app/shared/models/geographic-filter-region-model';
import { AuthService } from '../../auth/auth.service';
import { AuthUserType } from '../../auth/auth.types';
import { SearchResultsComponent } from '../../search/search-results/search-results.component';
import { EventService } from '../../services/event.service';
import { GeographicService } from '../../services/geographic.service';
import { PreviousRouteService } from '../../services/previous-route.service';
import { SearchService } from '../../services/search.service';
import { AppPermissions } from '../../shared/app.permissions';
import { AgencyService } from 'src/app/services/agency.service';
import { SlAgencyFilterComponent } from 'src/app/shared/components/sl-agency-filter/sl-agency-filter.component';
import { MultiSelectChangeEvent } from 'primeng/multiselect';

@Component({
  selector: 'ness-ballistic-evidence-list',
  templateUrl: './ballistic-evidence-list.component.html'
})
export class ballisticEvidenceListComponent implements OnInit, OnDestroy, AfterViewInit, AfterViewChecked {
  @ViewChild(SearchResultsComponent, { static: false }) searchTable: SearchResultsComponent;
  @ViewChild('slagencyfilter') slAgencyFilter: SlAgencyFilterComponent;
  private _crimeTypeName: string;
  private _userType: AuthUserType = this.authService.userType;
  readonly hasUserType: AuthUserType[] = ['ATFFieldDivision', 'ATFHQ'];
  event$: Observable<ServerModels.ENESS_EventDTO>;
  searchResults: ServerModels.SearchResultsViewModel[] = [];
  includeCDSFilter: boolean;
  localRowsPerPage: number = 5000;
  loadingEndRow: number = this.localRowsPerPage;
  loadingStartRow: number = 1;
  loading: boolean;
  messageTemplate: string;
  searching: boolean = false;
  showSidebar: boolean = false;
  showEventDetail: boolean = false;
  showNIBINCaseData: boolean = false;
  hasEditPermission: boolean = false;
  hasVirtualRelationToFirearm: boolean = false;
  hideAgencyTag: boolean = true;
  eventStream$: BehaviorSubject<any> = new BehaviorSubject(undefined);
  crimeGunCustomFields$: Observable<ServerModels.GunDetailCustomFields>;
  showFirearmData: boolean = false;
  fieldDivisionId: string = null;
  crimeGunFirearm$: Observable<ServerModels.CrimeGunFirearm>;
  errorMessage: string = 'An error occurred.';
  displayErrorStatus: boolean = false;
  currentUserType: string = AppUtils.getCurrentUserType();
  selectedResultRowIndex: number = -1;
  returnRouteNavigationExtras: NavigationExtras = null;

  readonly canEditCrimeGunFirearm: AuthorizationModel = AppPermissions.canEditCrimeGunFirearm;
  readonly canViewSLFilter: AuthorizationModel = AppPermissions.canViewSLFilter;
  readonly canViewTraceData = AppPermissions.canViewTraceData;
  readonly filterStorageKey: string = "ballisticEvidenceListFilter";
  readonly dateStorageKey: string = 'ballisticEvidenceDate';
  readonly includeCDSStorageKey: string = 'ballisticEvidenceIncludeCDS';
  @ViewChild('df', { static: false }) dateRangeFilterCmp: DateRangeFilterComponent;
  @ViewChild('gfr', { static: false }) geoFilterRegionCmp: GeographicFilterRegionComponent;
  @ViewChild('cf', { static: false }) includeCDSFilterCmp: IncludeCDSFilterComponent;
  readonly canView = AppPermissions.canViewGeoFilterRegion;
  geographicFilterRegion: GeographicFilterRegionModel = null;
  readonly isCDSGuest = this.authService.isCDSGuest();

  sites$: Observable<ServerModels.NibinSiteViewModel[]> = this.lookupService.nibinSites$;
  selectedSites: ServerModels.NibinSiteViewModel[] = [];
  userAgencies: ServerModels.AgencyViewModel[] = []
  userAgenciesCount: number = 0;
  selectedAgencyIds: string[] = [];
  siteIDList: string[] = [];
  selectedSiteValues: string = "All";
  dateRangeFilterLabel: string = "";
  showStatus: boolean = false;
  showResults: boolean = false;
  listForm: FormGroup = this.fb.group({
    DateField: ['', Validators.required],
    GeoField: ['', Validators.required],
    OnlyFirearms: false,
    OnlyNIBINLeads: false,
    PageNumber: 0,
    RowsPerPage: this.localRowsPerPage
  });

  private _searchRowUnselectSub$: Subscription;
  private _searchModel: ServerModels.BallasticEvidenceListParamsViewModel = JSON.parse(sessionStorage.getItem(this.filterStorageKey));
  get searchModel(): ServerModels.BallasticEvidenceListParamsViewModel {
    if (this._searchModel) {
      this._searchModel.PageNumber = this._searchModel.PageNumber || 0;
      this._searchModel.RowsPerPage = this._searchModel.RowsPerPage || this.localRowsPerPage;
    }
    return this._searchModel;
  }

  set searchModel(sm: ServerModels.BallasticEvidenceListParamsViewModel) {
    this._searchModel = sm;
    sessionStorage.setItem(this.filterStorageKey, JSON.stringify(sm));
  }
  get crimeTypeName(): string {
    return this._crimeTypeName;
  }

  set crimeTypeName(type: string) {
    this._crimeTypeName = type;
  }

  public get cdsVirtualGroups(): ServerModels.VirtualGroupViewModel[] {
    return this.authService.cdsVirtualGroups;
  }

  private _destroyed$: Subject<void> = new Subject();

  constructor(private searchService: SearchService,
    public crimeGunService: CrimeGunService,    
    public lookupService: LookupService,
    public geographicService: GeographicService,
    private agencyService: AgencyService,
    private eventService: EventService,
    private authService: AuthService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private previousRouteService: PreviousRouteService,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnInit() {
    if (!this.authService.hasRole(['NESS Administrator']) && this.authService.hasUserType(['StateLocalLE'])) {
      this.geographicService.geographicFilterModel$.next(null);
      this.listForm.controls["GeoField"].setValidators(null);
      this.listForm.controls["GeoField"].updateValueAndValidity();
      this.agencyService.GetAgenciesForCurrentStateAndLocalUser().pipe(take(1)).subscribe((ag: ServerModels.AgencyViewModel[]) => {
        this.userAgencies = ag;
        this.userAgenciesCount = ag.length;
        this.hideAgencyTag = (ag.length < 2);
      });
    }
    else {
      this.hideAgencyTag = false;
      this.userAgencies = [];
      this.userAgenciesCount = 0;
    }

    this.route.queryParams.pipe(takeUntil(this._destroyed$)).subscribe((params: Params) => {
      if (params && Object.keys(params).length > 0) {
        const type: SearchResultType = <SearchResultType>params['type'];
        const id: string = params['dataId'];
        const rowIndex = parseInt(params['rowIndex']) || -1;
        this.selectedResultRowIndex = rowIndex;
        if (type && id && rowIndex) {
          this.onShowDetails({ detailType: type, dataId: id, rowIndex: rowIndex });
        }
        this._clearRouteParams();
      }
    });
    this.showStatus = false;
    this.geographicFilterRegion = new GeographicFilterRegionModel('all', 'all', 'all', [], 'All', 'All', 'All', 'All', 'All', 'All', 'All', [], 'All', 'All', 'All', []);
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.showResults) {
        this._searchRowUnselectSub$ = this.searchTable.dataTable.onRowUnselect.subscribe((evt) => this._clearRouteParams());
        this.messageTemplate = 'Loading...';
        this.searchTable.dataTable.reset();
        this.searchResults = [];
        this.searchModel = this.listForm.value as ServerModels.BallasticEvidenceListParamsViewModel;
        if (this.searchModel && Object.keys(this.searchModel).length > 0) {
          this.listForm.setValue({
            DateField: this.searchModel.DateField || '',
            GeoField: '',
            OnlyFirearms: false,
            OnlyNIBINLeads: false,
            PageNumber: 0,
            RowsPerPage: this.localRowsPerPage
          });
        }
      }
    });
  }

  ngAfterViewChecked(): void {
    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy() {
    this._destroyed$.next();
    this._destroyed$.complete();
    this._searchRowUnselectSub$?.unsubscribe();
  }

  public isDateFilterAcqDate() {
    return this.listForm.get('DateField').value == 'NIBINAcquisitionDate';
  }

  public isDateFilterOccDate() {
    return this.listForm.get('DateField').value == 'NIBINOccurrenceDate';
  }

  public dateFilterVerbiage() {
    if (this.isDateFilterAcqDate()) return 'Filtering by NIBIN Acquisition Date Range:';
    else if (this.isDateFilterOccDate()) return 'Filtering by NIBIN Occurrence Date Range:';
    else return '';
  }

  public onSelectedResult(evt: { result: ServerModels.SearchResultsViewModel, resultType: SearchResultType, rowIndex: number }): void {
    switch (evt.resultType) {
      case SearchResultType.RMSEvent:
        this.returnRouteNavigationExtras = {
          queryParams: {
            'type': SearchResultType.RMSEvent,
            'dataId': evt.result.EventID,
            'rowIndex': evt.rowIndex
          }
        } as NavigationExtras;
        this._viewEventDetails(evt.result);
        break;
      case SearchResultType.FirearmData:
        this._viewFirearmDetails(evt.result);
        break;
      case SearchResultType.NIBINCaseData:
        this.returnRouteNavigationExtras = {
          queryParams: {
            'rowIndex': evt.rowIndex
          }
        };
        this._viewNIBINCaseData(evt.result);
        break;
      case SearchResultType.CrimeGunDetail:
        this.returnRouteNavigationExtras = {
          queryParams: {
            'rowIndex': evt.rowIndex
          }
        };
        let returnUrlTree: UrlTree = this.router.createUrlTree(['/search'], this.returnRouteNavigationExtras);
        this.previousRouteService.returnUrl = this.router.serializeUrl(returnUrlTree);
        break;
    };
  }

  public onShowDetails(evt: { detailType: SearchResultType, dataId: string, rowIndex?: number }): void {
    this.clearSidebar();
    switch (evt.detailType) {
      case SearchResultType.RMSEvent:
        this.returnRouteNavigationExtras = {
          queryParams: {
            'type': SearchResultType.RMSEvent,
            'dataId': <string>evt.dataId,
            'rowIndex': evt.rowIndex || parseInt(this.returnRouteNavigationExtras?.queryParams['rowIndex'])
          }
        } as NavigationExtras;
        this._getEventDetails(<string>evt.dataId);
        break;
      case SearchResultType.FirearmData:
          let firearmId = evt.dataId;
          if (!!firearmId) {
            this.showCrimeGunCustomFields(firearmId);
          }
        break;
      case SearchResultType.CrimeGunDetail:
        this.returnRouteNavigationExtras = {
          queryParams: {
            'rowIndex': (evt.rowIndex || parseInt(this.returnRouteNavigationExtras?.queryParams['rowIndex']))
          }
        };
        let returnUrlTree: UrlTree = this.router.createUrlTree(['/search'], this.returnRouteNavigationExtras);
        this.previousRouteService.returnUrl = this.router.serializeUrl(returnUrlTree);
        break;
    }
  }

  private _getEventDetails(eventId: string): void {
    this.event$ = this.eventService.getEventDataModelById(eventId).pipe(tap((x: ServerModels.ENESS_EventDTO) => {
      this.showSidebar = this.showEventDetail = true;
    }));
  }

  private _viewNIBINCaseData(result: ServerModels.SearchResultsViewModel): void {
    if (result.NIBINCaseID) {
      this.crimeGunService.getNIBINCaseData(result.NIBINCaseID);
      this.showSidebar = this.showNIBINCaseData = true;
    }
  }

  private _viewEventDetails(result: ServerModels.SearchResultsViewModel): void {
    if (result.EventID) {
      this.crimeTypeName = result.CrimeTypeDesc || '';
      this._getEventDetails(result.EventID);
    }
  }


  private _viewFirearmDetails(result: ServerModels.SearchResultsViewModel): void {
    let firearmId = result.FirearmID;
    this.showCrimeGunCustomFields(firearmId);
  }

  private _getSearchResults(): void {
    this.loading = true;
    this.searching = true;

    this.dateRangeFilterCmp.applyFilters();
    let storedDateFilter = JSON.parse(sessionStorage.getItem(this.dateStorageKey));

    // Note: HTML template may have not rendered this due to business rule
    if (this.includeCDSFilterCmp) {
      this.includeCDSFilterCmp.applyFilters();
      this.includeCDSFilter = JSON.parse(sessionStorage.getItem(this.includeCDSStorageKey));
    }
    else {
      // Business logic determined `!isCDSGuest`, so remove the session key value
      sessionStorage.removeItem(this.includeCDSStorageKey);
    }

    let sd: Date = new Date(storedDateFilter["_startDate"])
    sd.setUTCHours(0);
    this.searchModel.StartDate = sd;
    let ed: Date = new Date(storedDateFilter["_endDate"])
    ed.setUTCHours(23, 59, 59, 999);
    this.searchModel.EndDate = ed;
    this.searchModel.OnlyFirearms = !!this.listForm.get('OnlyFirearms').value;
    this.searchModel.OnlyNIBINLeads = !!this.listForm.get('OnlyNIBINLeads').value;
    this.searchModel.IncludeCDS = this.includeCDSFilter;
 
    if (this.listForm.get('GeoField').value == 'NIBINSite') {
      this.searchModel.NIBINSiteCode = this.siteIDList;
    } else if (!this.authService.hasRole(['NESS Administrator']) && this.authService.hasUserType(['StateLocalLE'])) {
      this.searchModel.AgencyID = this.selectedAgencyIds;
    } else {
      this.searchModel.FieldDivisionID = this.geographicFilterRegion?.fieldDivisionID && this.geographicFilterRegion?.fieldDivisionID != 'all' ? this.geographicFilterRegion?.fieldDivisionID : null;
      this.searchModel.FieldDivisionGroupID = this.geographicFilterRegion?.fdgroups && this.geographicFilterRegion?.fdgroups.length > 0 ? this.geographicFilterRegion?.fdgroups.map(g => g.IntelligenceReportATFReferralGroupsID) : null;
      this.searchModel.StateID = this.geographicFilterRegion?.stateID && this.geographicFilterRegion?.stateID != 'all' ? this.geographicFilterRegion?.stateID : null;
      this.searchModel.CountyID = this.geographicFilterRegion?.counties && this.geographicFilterRegion?.counties.length > 0 ? this.geographicFilterRegion?.counties.map(c => c.CountyID) : null;
      this.searchModel.AgencyID = this.geographicFilterRegion?.agencies && this.geographicFilterRegion?.agencies.length > 0 ? this.geographicFilterRegion?.agencies.map(a => a.AgencyID) : null;
    }

    this.buildDateRangeFilterString();

    this.searchService.getballisticEvidenceListResults(this.searchModel).pipe(take(1)).subscribe({
      next: (results: ServerModels.SearchResultsViewModel[]) => {
        if (results == null || typeof (results) === 'undefined') {
          this.loading = false;
          this.searching = false;
        }

        this.loading = results.length > 0;
        this.searching = false;
        if (results.length > 0) {
          this.loadingStartRow = this.searchModel.PageNumber == 0 ? 1 : (1 + this.searchResults.length);
          this.searchResults = this.searchResults.concat(results);
          this.loadingEndRow = this.searchResults.length;
          this.messageTemplate = `Loaded rows ${this.loadingStartRow} thru ${this.loadingEndRow}...`;
          if (this.loading = results.length >= this.localRowsPerPage) {
            this.searchModel.PageNumber += 1;
            this._getSearchResults();
          }
        }
      },
      error: (err:any) => {
        this.loading = false;
        this.searching = false;
      },
      complete: () => this.searchTable.selectedResultRow = !!this.selectedResultRowIndex ? this.searchResults[this.selectedResultRowIndex] : null
    });
  }

  private _clearRouteParams(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: null
    });
  }

  public onClear(): void {
    // Remove all saved values
    sessionStorage.removeItem(this.filterStorageKey);
    sessionStorage.removeItem(this.dateStorageKey);
    sessionStorage.removeItem(this.includeCDSStorageKey);

    // Reset all form fields and internal state
    this.searchModel = null;
    this.listForm.reset({ DateField: '', GeoField: '' });
    this.dateRangeFilterCmp?.resetToDefaultFilters();
    this.geoFilterRegionCmp?.resetToDefaultFilters();
    this.includeCDSFilterCmp?.resetToDefaultFilters();
    this.showSidebar = false;
    this.loading = false;
    this.searching = false;
    this.searchResults = [];
    this.loadingStartRow = 1;
    this.loadingEndRow = this.localRowsPerPage;
    this.selectedResultRowIndex = -1;
    if (!!this.searchTable) {
      this.searchTable.selectedResultRow = null;
      this.searchTable?.dataTable?.reset();
      sessionStorage.removeItem(this.searchTable.tableStateKey);
    }
    this.showStatus = false;
    this.showResults = false;
    this.selectedSites = [];
    this.selectedAgencyIds = [];
    this.siteIDList = [];
    this.selectedSiteValues = null;
    if (this.authService.hasUserType(['StateLocalLE'])) {
      this.hideAgencyTag = !this.authService.hasRole(['NESS Administrator']) ? (this.userAgenciesCount < 2) : false;
      if (this.slAgencyFilter) {
        this.selectedAgencyIds = [];
        this.slAgencyFilter.clearSelections();
      }
    }
    this._clearRouteParams();
  }

  public onSearch(): void {
    this.showResults = true;
    this.messageTemplate = 'Loading...';
    this.loadingStartRow = 1;
    this.loadingEndRow = this.localRowsPerPage;
    this.searchResults = [];
    this.selectedResultRowIndex = -1;
    this.searchTable?.dataTable?.reset();
    this.searchModel = this.listForm.value as ServerModels.BallasticEvidenceListParamsViewModel;
    this.searchModel.PageNumber = 0;
    this.searchModel.RowsPerPage = this.localRowsPerPage;
    this.loadingStartRow = 1;
    this.loadingEndRow = this.searchModel.RowsPerPage;
    let regionFilterValues = this.geoFilterRegionCmp?.getSelectedFilterValues();
    
    if (!!regionFilterValues) {     
      this.geographicFilterRegion = regionFilterValues;
      sessionStorage.setItem(this.filterStorageKey, JSON.stringify(this.geographicFilterRegion));
    }
    this.showStatus = true;
    this._getSearchResults();
  }

  public onShowSidebar(sideBar: Sidebar): void {
    window.setTimeout(() => {
      (<HTMLElement>sideBar.el.nativeElement).setAttribute("tabindex", "-1");
      (<HTMLElement>sideBar.el.nativeElement).focus();
    }, 1000);
  }

  public showCrimeGunCustomFields(firearmId: string) {
    this.showSidebar = this.showFirearmData = true;
    this.crimeGunCustomFields$ = this.crimeGunService.getCrimeGunCustomFields(firearmId).pipe(takeUntil(this._destroyed$), tap(x => {
      this.hasVirtualRelationToFirearm = !this.authService.hasUserType(['StateLocalLE']) || this.authService.canEdit(x.VirtualGroupRelation)
    }));
  }

  public closeFirearmView(): void {
    this.clearSidebar()
  }

  public onFirearmUpdate(data: ServerModels.GunDetailCustomFields): void {
    if (data == null) {
      return;
    }
    this.crimeGunService.addOrUpdateCustomCrimeGunFields(data).subscribe((customFields: ServerModels.GunDetailCustomFields) => {
      this.crimeGunFirearm$ = this.crimeGunService.getCrimeGunFirearm(data.CrimeGunNumberID).pipe(
        switchMap((firearm: ServerModels.CrimeGunFirearm) => {
          if (!!customFields.FirearmTraceData.length) {
            firearm.FTSIDs = customFields.FirearmTraceData.map(x => x.FTSID)?.join("; ");
            firearm.FirearmType = customFields.FirearmTraceData[0].FTSType;
            firearm.PurchaseCity = customFields.FirearmTraceData[0].PurchaseCity;
            firearm.PurchaseDate = customFields.FirearmTraceData[0].PurchaseDate;
            firearm.PurchaseState = customFields.FirearmTraceData[0].PurchaseState;
            firearm.FTSTypeID = customFields.FirearmTraceData[0].FTSTypeID;
          }

          return of(firearm);
        }));
    },
      (err:any) => {
        this.errorMessage = err;
        this.displayErrorStatus = true;
      },
      () => this.clearSidebar()
    );
  }

  public clearSidebar(): void {
    this.showEventDetail = false;
    this.showNIBINCaseData = false;
    this.showFirearmData = false;
    this.showSidebar = false;
    this._clearRouteParams();
  }

  public onSelectSites(evt: { originalEvent: Event, value: ServerModels.NibinSiteViewModel[], itemValue?: ServerModels.NibinSiteViewModel }): void {
    this.siteIDList = evt.value.map(site=>site.Code) || [];
    this.selectedSiteValues = evt.value.map(site => site.ShortName).join("; ");
  }

  public buildDateRangeFilterString() {
    this.dateRangeFilterLabel = this.isDateFilterAcqDate() ? "Acq " : (this.isDateFilterOccDate() ? "Occ " : "")
  }

  public onSelectedAgencies(agvm: string | MultiSelectChangeEvent): void {
    if (typeof(agvm) === 'string' && agvm === 'All') {
      this.selectedAgencyIds = [];
      this.geographicFilterRegion.agencyName = "All";
    }
    if(typeof(agvm) === 'object' && Object.hasOwn(agvm, 'originalEvent')) {
      this.selectedAgencyIds = agvm.value.map((a: ServerModels.AgencyViewModel) => a.AgencyID)
      if (agvm.value.length > 0) {
        let agencies: string[] = [];
        agencies = agvm.value.map(a => a.AgencyName);
        this.geographicFilterRegion.agencyName = agencies.join('; ');
      } else {
        this.geographicFilterRegion.agencyName = "All";
      }
    }
  }
}
